import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { getSolution, SOLUTION_DETAILS } from "../../data/solutions";
import { SolutionClip } from "../../components/site/SolutionClip";
import { getService } from "../../data/services";
import NotFound from "./NotFound";

const SectionTitle = ({ no, children }) => (
  <Reveal>
    <div className="flex items-center gap-4 mb-7">
      <span className="font-marcellus text-nx-gold/60 text-lg">{no}</span>
      <h2 className="font-marcellus text-2xl sm:text-3xl text-white">{children}</h2>
    </div>
  </Reveal>
);

export default function SolutionDetail() {
  const { slug } = useParams();
  const solution = getSolution(slug);
  if (!solution) return <NotFound />;

  const idx = SOLUTION_DETAILS.findIndex((s) => s.slug === slug);
  const next = SOLUTION_DETAILS[(idx + 1) % SOLUTION_DETAILS.length];

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid={`solution-detail-${slug}`}>
      <SiteHeader />
      <PageHero crumb="Solutions" eyebrow={`Solution ${String(idx + 1).padStart(2, "0")}`} title={solution.name} description={solution.tagline} />
      <SolutionClip slug={slug} />
      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.4fr_1fr] gap-14">
          <div className="flex flex-col gap-14">
            <div>
              <SectionTitle no="01">The business challenge</SectionTitle>
              <Reveal delay={0.05}><p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="solution-challenge">{solution.challenge}</p></Reveal>
            </div>
            <div>
              <SectionTitle no="02">Our approach</SectionTitle>
              <Reveal delay={0.05}><p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="solution-approach">{solution.approach}</p></Reveal>
            </div>
            <div>
              <SectionTitle no="03">Solution components</SectionTitle>
              <Stagger className="flex flex-col gap-px bg-white/10 border border-white/10">
                {solution.components.map((c, i) => (
                  <StaggerItem key={i}>
                    <div className="bg-nx-navy px-6 py-4 text-sm sm:text-base text-slate-200 leading-relaxed" data-testid={`solution-component-${i}`}>{c}</div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
            <div>
              <SectionTitle no="04">What we deliver</SectionTitle>
              <Stagger className="flex flex-col gap-px bg-white/10 border border-white/10">
                {solution.deliverables.map((d, i) => (
                  <StaggerItem key={d}>
                    <div className="flex items-center gap-5 bg-nx-navy px-6 py-5" data-testid={`solution-deliverable-${i}`}>
                      <span className="flex items-center justify-center w-7 h-7 border border-nx-gold/40 shrink-0"><Check size={14} className="text-nx-goldlight" /></span>
                      <span className="text-sm sm:text-base text-slate-200">{d}</span>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
            <div>
              <SectionTitle no="05">What it means for you</SectionTitle>
              <Stagger className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {solution.benefits.map((b, i) => (
                  <StaggerItem key={i}>
                    <div className="h-full bg-nx-navy2 p-6 text-sm text-slate-300 leading-relaxed" data-testid={`solution-benefit-${i}`}>{b}</div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
          <div className="flex flex-col gap-8">
            <Reveal delay={0.15}>
              <div className="border border-white/10 bg-white/[0.03] p-7">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-5">Delivered through</p>
                <div className="flex flex-col gap-3">
                  {solution.related.map((rs) => {
                    const svc = getService(rs);
                    return svc ? (
                      <Link key={rs} to={`/services/${rs}`} className="group flex items-center justify-between border border-white/10 hover:border-nx-gold/50 px-5 py-3.5 transition-colors" data-testid={`solution-related-${rs}`}>
                        <span className="text-sm text-slate-200">{svc.name}</span>
                        <ArrowRight size={14} className="text-slate-500 group-hover:text-nx-goldlight group-hover:translate-x-1 transition-all" />
                      </Link>
                    ) : null;
                  })}
                </div>
                <p className="mt-6 text-xs text-slate-500 leading-relaxed">Supported for the long term — AMC, remote and on-site support across Kerala.</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to={`/solutions/${next.slug}`} className="group block border border-white/10 hover:border-nx-gold/50 p-7 transition-colors" data-testid="solution-next-link">
                <span className="text-[10px] tracking-[0.28em] uppercase text-slate-500">Next solution</span>
                <span className="flex items-center justify-between mt-3">
                  <span className="font-marcellus text-xl text-white">{next.name}</span>
                  <ArrowRight size={18} className="text-nx-goldlight transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.25}>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors" data-testid="solution-back-link">
                <ArrowLeft size={14} /> All solutions
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand title={`Planning ${solution.name.toLowerCase()} for your site?`} />
      <SiteFooter />
    </div>
  );
}
